import React from 'react';
import { useHistory } from 'react-router-dom';
import { Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import {
  ALL_REGIONS_VALUE,
  LICENCE_REGION_OPTIONS,
  MAX_RANGE_DAYS,
  OPPORTUNITY_REGION_OPTIONS,
  RANGE_OPTIONS,
  RANGE_TYPES,
  addDaysToInputDate,
  buildMonthOptions,
  errorText,
  istTodayInputDate,
  rangeError,
  timezoneOptionsForRegion,
} from './constants';

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  background: none;
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  color: ${COLORS.BOSTON_BLUE};
  font-size: 13px;
  line-height: 18px;

  &:hover {
    background: ${COLORS.GRAY_LIGHT};
  }
`;

// Sticky so the title and the back link survive a long table scroll.
// Sticky so the title and the way back survive a long scroll. z-index stays under
// the table's own sticky header (8), which scrolls in its own container.
const HeaderRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  margin-bottom: 8px;
  background: ${COLORS.WHITE};
  border-bottom: 1px solid ${COLORS.GRAY_LIGHT};
`;

// The breadcrumb matches paths exactly, so a nested screen shows no trail back.
export const BackToEval = () => {
  const history = useHistory();

  return (
    <BackLink type="button" onClick={() => history.push('/eval/')}>
      <span aria-hidden="true">←</span> Eval
    </BackLink>
  );
};

export const PageHeader = ({ title, right }) => (
  <HeaderRow>
    <BackToEval />
    <Text variant="body1Medium">{title}</Text>
    {right}
  </HeaderRow>
);

const FilterRow = styled(Row)`
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterLabel = styled(Text)`
  margin-right: 8px;
  white-space: nowrap;
`;

const ALL_SPECIALISTS_VALUE = 'all';

export const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
  color: ${({ color }) => color};
  border: 1px solid ${({ color }) => color};
`;

export const MonoText = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

export const ClickableNumber = styled.span`
  font-variant-numeric: tabular-nums;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ disabled }) => (disabled ? COLORS.GRAY : COLORS.BLACK)};
  text-decoration: ${({ disabled }) => (disabled ? 'none' : 'underline')};
`;

export const MonthPicker = ({ value, onChange }) => {
  const options = React.useMemo(() => buildMonthOptions(), []);

  const handleChange = React.useCallback(
    (_, { value: next }) => {
      onChange(next);
    },
    [onChange],
  );

  return (
    <Row alignItems="center">
      <FilterLabel variant="body3" color={COLORS.GRAY}>
        Month
      </FilterLabel>
      <Dropdown
        compact
        selection
        selectOnBlur={false}
        options={options}
        value={value}
        onChange={handleChange}
      />
    </Row>
  );
};

const RangeFields = styled(Row)`
  align-items: center;
  gap: 8px;

  input[type='date'] {
    padding: 7px 9px;
    border: 1px solid ${COLORS.GRAY_LIGHT};
    border-radius: 4px;
    font-size: 13px;
    font-family: inherit;
  }
`;

/**
 * Window picker shared by Availability and Capacity.
 *
 * Holds the dates locally and only lifts a custom range once it is valid, so a
 * half-typed range never fires a request the gateway would reject.
 */
export const RangePicker = ({ value, onChange, showMonth = true, withTime = false }) => {
  const { rangeType, month, startDate, endDate } = value;
  const options = React.useMemo(
    () => RANGE_OPTIONS.filter(option => showMonth || option.value !== RANGE_TYPES.month),
    [showMonth],
  );
  const monthOptions = React.useMemo(() => buildMonthOptions(), []);
  // Two blank dates is a screen's default window, not a half-filled range to scold about.
  const error =
    rangeType === RANGE_TYPES.custom && (startDate || endDate)
      ? rangeError(startDate, endDate, withTime)
      : null;

  const handleType = React.useCallback(
    (_, { value: next }) => {
      if (next !== RANGE_TYPES.custom) {
        onChange({ ...value, rangeType: next });

        return;
      }
      // Seed a valid week so switching to Date range shows data straight away.
      const today = istTodayInputDate();
      const from = startDate || (withTime ? `${today}T00:00` : today);
      onChange({
        ...value,
        rangeType: next,
        startDate: from,
        endDate: endDate || (withTime ? `${today}T23:59` : addDaysToInputDate(from, 6)),
      });
    },
    [onChange, value, startDate, endDate, withTime],
  );

  return (
    <Row alignItems="center" style={{ gap: '12px' }}>
      <Row alignItems="center">
        <FilterLabel variant="body3" color={COLORS.GRAY}>
          Showing
        </FilterLabel>
        <Dropdown
          compact
          selection
          selectOnBlur={false}
          options={options}
          value={rangeType}
          onChange={handleType}
          aria-label="Date range preset"
        />
      </Row>

      {rangeType === RANGE_TYPES.month && (
        <Dropdown
          compact
          selection
          selectOnBlur={false}
          options={monthOptions}
          value={month}
          onChange={(_, { value: next }) => onChange({ ...value, month: next })}
          aria-label="Month"
        />
      )}

      {rangeType === RANGE_TYPES.custom && (
        <RangeFields gutter={0}>
          <input
            type={withTime ? 'datetime-local' : 'date'}
            value={startDate || ''}
            max={withTime ? undefined : endDate || undefined}
            onChange={event => onChange({ ...value, startDate: event.target.value })}
            aria-label={withTime ? 'Start date and time' : 'Start date'}
          />
          <Text variant="body3" color={COLORS.GRAY}>
            to
          </Text>
          <input
            type={withTime ? 'datetime-local' : 'date'}
            value={endDate || ''}
            min={withTime ? startDate || undefined : startDate || undefined}
            max={
              withTime || !startDate
                ? undefined
                : addDaysToInputDate(startDate, MAX_RANGE_DAYS - 1)
            }
            onChange={event => onChange({ ...value, endDate: event.target.value })}
            aria-label={withTime ? 'End date and time' : 'End date'}
          />
          {error && (
            <Text variant="body3" color={COLORS.RUSTY_RED}>
              {error}
            </Text>
          )}
        </RangeFields>
      )}
    </Row>
  );
};

/* compact + search shrinks the box to the query as you type, so the width is pinned
   to keep the options readable and the control still. */
const ZoneWrapper = styled.div`
  .ui.dropdown {
    min-width: 190px;
    width: 190px;
  }

  .ui.dropdown .menu {
    width: auto;
    min-width: 220px;
  }

  .ui.dropdown .menu > .item {
    white-space: nowrap;
  }
`;

/**
 * Timezone the window and its times are read in.
 *
 * Scoped to the region on screen, since the zones worth reading are the ones that
 * region's parents sit in. IST leads every list because the coaches read from India.
 */
export const TimezonePicker = ({ value, onChange, region = null }) => {
  const options = React.useMemo(() => timezoneOptionsForRegion(region), [region]);

  // Narrowing the region can strand a zone that is no longer offered.
  React.useEffect(() => {
    if (!options.some(option => option.value === value)) onChange(options[0].value);
  }, [options, value, onChange]);

  return (
    <ZoneWrapper>
      <Dropdown
        selection
        search
        selectOnBlur={false}
        options={options}
        value={value}
        onChange={(_, { value: next }) => onChange(next)}
        aria-label="Timezone"
      />
    </ZoneWrapper>
  );
};

export const SpecialistPicker = ({ specialists, value, onChange }) => {
  const options = React.useMemo(
    () => [
      {
        key: ALL_SPECIALISTS_VALUE,
        value: ALL_SPECIALISTS_VALUE,
        text: 'All specialists',
      },
      ...(specialists || []).map(specialist => ({
        key: specialist.teacher_id,
        value: specialist.teacher_id,
        text: specialist.teacher_name || specialist.teacher_id,
      })),
    ],
    [specialists],
  );

  const handleChange = React.useCallback(
    (_, { value: next }) => {
      onChange(next === ALL_SPECIALISTS_VALUE ? null : next);
    },
    [onChange],
  );

  return (
    <Row alignItems="center">
      <FilterLabel variant="body3" color={COLORS.GRAY}>
        Specialist
      </FilterLabel>
      <Dropdown
        search
        selection
        selectOnBlur={false}
        options={options}
        value={value || ALL_SPECIALISTS_VALUE}
        onChange={handleChange}
        placeholder="All specialists"
      />
    </Row>
  );
};

/**
 * Region the screen is scoped to.
 *
 * A coach holds a licence region and a parent sits in an opportunity region, so the
 * two are different lists; `subject` picks which one a screen is asking about.
 */
export const RegionPicker = ({ value, onChange, subject = 'licence' }) => {
  const options =
    subject === 'opportunity' ? OPPORTUNITY_REGION_OPTIONS : LICENCE_REGION_OPTIONS;

  const handleChange = React.useCallback(
    (_, { value: next }) => {
      onChange(next === ALL_REGIONS_VALUE ? null : next);
    },
    [onChange],
  );

  return (
    <Row alignItems="center">
      <FilterLabel variant="body3" color={COLORS.GRAY}>
        Region
      </FilterLabel>
      <Dropdown
        compact
        selection
        selectOnBlur={false}
        options={options}
        value={value || ALL_REGIONS_VALUE}
        onChange={handleChange}
        aria-label="Region"
      />
    </Row>
  );
};

export const ErrorLine = ({ error }) => {
  const message = errorText(error);

  if (!message) return null;

  return (
    <Row>
      <Text variant="body2" color={COLORS.RUSTY_RED}>
        {message}
      </Text>
    </Row>
  );
};

export { ALL_SPECIALISTS_VALUE, FilterRow };
